import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import BaseOverviewStore, {
    type ApiResponse,
    type BaseStoreState,
} from '../../../../shared/stores/baseOverviewStore';
import { formatName } from '../../../../utils/helper';
import { MatenRequestResponse } from '../../../../shared';

interface OrderStoreState extends BaseStoreState {
    filterManager: any;
    filterEmployees: any;
    filterObject: any;
    filterStatus: string | null;
    filterDelievered: boolean | null;
}

/**
 * Load all orders and make it possible to manage them
 */
class OrderOverviewStore extends BaseOverviewStore<any> {
    protected storageKey(): string {
        return 'order';
    }

    // Implement required abstract methods
    protected defaultSortField(): string {
        return 'id';
    }

    protected getApiEndpoint(): string {
        return 'order';
    }

    protected getApiPath(): string {
        return 'query';
    }

    // Order-specific properties
    public refreshTimer: any = null;
    public available_managers: Array<{ id: number; name: string }> = [];
    public filterManager: any = null;
    public available_employees: Array<{ id: number; name: string }> = [];
    public filterEmployees: any = null;
    public available_objects: Array<{ id: number; name: string }> = [];
    public filterObject: any = null;
    public filterStatus: string | null = null;
    public filterDelievered: boolean | null = null;

    constructor(sdk: any) {
        super(sdk);

        // Override default sort order for orders
        this.sortOrder = -1;

        // Make order-specific properties observable
        makeObservable(this, {
            refreshTimer: observable,
            available_managers: observable,
            filterManager: observable,
            available_employees: observable,
            filterEmployees: observable,
            available_objects: observable,
            filterObject: observable,
            filterStatus: observable,
            filterDelievered: observable,
            refresh: action,
            resetRefreshTimer: action,
            applyFilterManager: action,
            applyfilterEmployees: action,
            applyFilterObject: action,
            applyFilterStatus: action,
            applyFilterDelivered: action,
            fetchAvailableManagers: action,
            fetchAvailableEmployees: action,
            fetchAvailableObjects: action,
            deleteOrder: action,
            allOrders: computed,
        });

        // Re-load from storage now that observables are set up
        this.loadFromLocalStorage();
    }

    protected buildApiParams(page: number): any {
        return {
            sortvalue: this.sortField,
            sort: this.sortOrder === 1 ? 'asc' : 'desc',
            search: this.searchValue || '',
            page,
            ...(this.filterManager?.id
                ? {
                      manager_id: this.filterManager?.id,
                  }
                : {}),
            ...(this.filterEmployees?.id
                ? {
                      employee_id: this.filterEmployees?.id,
                  }
                : {}),
            ...(this.filterObject?.id
                ? {
                      object_id: this.filterObject?.id,
                  }
                : {}),
            ...(this.filterStatus
                ? {
                      status: this.filterStatus,
                  }
                : {}),
            ...(this.filterDelievered !== null
                ? {
                      delivered: this.filterDelievered,
                  }
                : {}),
        };
    }

    protected processApiResponse(response: ApiResponse): any[] {
        return response.json.data.map((item: any) => {
            return {
                ...item,
                employee:
                    item?.employee_id === null
                        ? '-'
                        : formatName(item?.employee_firstname, item?.employee_lastname),
            };
        });
    }

    protected getStorageState(): OrderStoreState {
        return {
            sortOrder: this.sortOrder,
            sortField: this.sortField,
            searchValue: this.searchValue,
            filterManager: this.filterManager,
            filterEmployees: this.filterEmployees,
            filterObject: this.filterObject,
            filterStatus: this.filterStatus,
            filterDelievered: this.filterDelievered,
        };
    }

    protected applyStorageState(state: OrderStoreState): void {
        runInAction(() => {
            this.sortOrder = state.sortOrder || -1;
            this.sortField = state.sortField || this.defaultSortField();
            this.searchValue = state.searchValue || '';
            this.filterManager = state.filterManager || null;
            this.filterEmployees = state.filterEmployees || null;
            this.filterObject = state.filterObject || null;
            this.filterStatus = state.filterStatus || null;
            this.filterDelievered = state.filterDelievered ?? null;
        });
    }

    // Order-specific methods
    public resetRefreshTimer(): void {
        if (this.refreshTimer) {
            clearTimeout(this.refreshTimer);
            this.refreshTimer = null;
        }
    }

    public refresh(): void {
        if (this.refreshTimer) {
            return;
        }
        const that = this;
        this.refreshTimer = setTimeout(() => that.resetRefreshTimer(), 5000);
        this.fetchList();
    }

    public applyFilterManager(manager: any | null): void {
        this.filterManager = manager;
        this.saveToLocalStorage();
        this.fetchList();
    }

    public applyfilterEmployees(employee: any | null): void {
        this.filterEmployees = employee;
        this.saveToLocalStorage();
        this.fetchList();
    }

    public applyFilterObject(obj: any | null): void {
        this.filterObject = obj;
        this.saveToLocalStorage();
        this.fetchList();
    }

    public applyFilterStatus(status: string | null): void {
        this.filterStatus = status;
        this.saveToLocalStorage();
        this.fetchList();
    }

    public applyFilterDelivered(delivered: boolean | null): void {
        this.filterDelievered = delivered;
        this.saveToLocalStorage();
        this.fetchList();
    }

    public async fetchAvailableManagers(force: boolean = false): Promise<void> {
        if (!force && this.available_managers.length > 1) {
            return;
        }
        const managerResponse: any = await this.sdk.fetchApi('manager', 'query', {});

        runInAction(() => {
            if (managerResponse?.status === 200) {
                this.available_managers = managerResponse.json.data.map((manager: any) => ({
                    id: manager.id,
                    name: formatName(manager.firstname, manager.lastname),
                }));
            }
        });
    }

    public async fetchAvailableEmployees(force: boolean = false): Promise<void> {
        if (!force && this.available_employees.length > 1) {
            return;
        }
        const employeeResponse: any = await this.sdk.fetchApi('employee', 'query', {
            format: 'simple',
            limit: 999,
        });

        runInAction(() => {
            if (employeeResponse?.status === 200) {
                this.available_employees = employeeResponse.json.data.map((employee: any) => ({
                    id: employee.id,
                    name: formatName(employee.firstname, employee.lastname),
                }));
            }
        });
    }

    public async fetchAvailableObjects(force: boolean = false): Promise<void> {
        if (!force && this.available_objects.length > 1) {
            return;
        }
        const objResponse: any = await this.sdk.fetchApi('object', 'query', {
            format: 'simple',
            limit: 999,
        });

        runInAction(() => {
            if (objResponse?.status === 200) {
                this.available_objects = objResponse.json.data.map((obj: any) => ({
                    id: obj.id,
                    name: obj.name,
                }));
            }
        });
    }

    public async deleteOrder(order_id: number): Promise<MatenRequestResponse> {
        if (order_id) {
            try {
                const responseQuestion = await this.sdk.fetchApi('order', 'delete', {
                    order_id,
                });
                if (responseQuestion?.status === 200) {
                    this.fetchList(this.latestPage);
                    return { success: true, status: 200, data: responseQuestion.json };
                } else {
                    const errorCode = responseQuestion?.json?.error || '';

                    if (errorCode) {
                        switch (errorCode) {
                            case 'ERR_ORDER_NOT_FOUND':
                                throw new Error('Die Bestellung wurde nicht gefunden.');
                            default:
                                break;
                        }
                    }

                    throw new Error('Ein unbekannter Fehler ist aufgetreten');
                }
            } catch (error: any) {
                return {
                    success: false,
                    status: 500,
                    error: error.message || 'Ein unbekannter Fehler ist aufgetreten',
                };
            }
        }
        return { success: false, status: 400, error: 'Ungültige Bestell-ID' };
    }

    public async onDialogLeave(dialog: string, event: any = null): Promise<void> {
        if (dialog === 'order' && event?.event === 'SAVED') {
            await this.fetchList(this.latestPage);
        } else if (
            dialog === 'editorder' &&
            (event?.event === 'UPDATE' || event?.event === 'NEW')
        ) {
            await this.fetchList(this.latestPage);
        } else {
            await super.onDialogLeave(dialog, event);
        }
    }

    public onLeaveStore(): void {
        super.onLeaveStore();

        if (this.refreshTimer) {
            clearTimeout(this.refreshTimer);
        }
    }

    // Computed properties and aliases for compatibility
    get allOrders(): Array<any> | null {
        if (!this.list) return null;
        return this.list;
    }

    get orderList(): Array<any> {
        return this.list;
    }

    get orderListLoading(): boolean {
        return this.listLoading;
    }

    public fetchOrderList(page: number = 1): Promise<void> {
        return this.fetchList(page);
    }
}

export default OrderOverviewStore;
